import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input, type InputProps } from '@/components/ui/input';
import { Kbd } from '@/components/Kbd';
import { useFindShortcut } from '@/hooks/use-find-shortcut';
import { findShortcutLabel, isMacPlatform } from '@/lib/find-shortcut';
import { cn } from '@/lib/utils';

export type SearchInputProps = Omit<InputProps, 'type'> & {
  /**
   * Shows an X that empties the box while it has text, and lets Escape empty it. Called instead of an
   * onChange: set the search to '' there (e.g. `search.setText('', { now: true })`). Focus stays in the box.
   * Escape empties it even in a dialog, which stays open (the next Escape closes it), and isn't passed
   * on to `onKeyDown`.
   */
  onClear?: () => void;
  /** Classes for the wrapper: width and placement (`w-56`, `flex-1`, `min-w-[14rem]`). */
  className?: string;
  /**
   * A white (`bg-background`) fill, for a box that sits straight on a tinted page (the wholesale
   * portal's catalogue on its muted background), where the see-through field would take the tint.
   */
  filled?: boolean;
  /**
   * Marks a search in force while the box has text: primary border, tint and magnifier, so a filtered
   * list stands out from an empty box, focused or not (AddFilterBar's search uses it).
   */
  highlightActive?: boolean;
  /**
   * The page's search: ⌘F / Ctrl+F comes here, and a second press opens the browser's own find. One
   * box per page (or per dialog) answers, the first; it shows the shortcut as a key chip while empty,
   * on desktop. Not for a box in a picker or menu. AddFilterBar's search has it on.
   */
  findShortcut?: boolean;
};

const ICON_SIZE = { default: 'size-4', sm: 'size-4', compact: 'size-3.5' } as const;

/**
 * A search box: the magnifier on the left, an optional clear button on the right, sized like Input
 * (`size` default 36, sm 32 in filter bars and table toolbars, compact 28 in dense panels).
 *
 * It has no visible label, so give it an `aria-label` ("Search products"), or an id with a Label.
 * Everything else an Input takes (value, onChange, onKeyDown, `{...search.inputProps}`, combobox
 * aria props) goes to the input; `className` places the wrapper.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    className,
    filled = false,
    highlightActive = false,
    findShortcut = false,
    size,
    onClear,
    value,
    disabled,
    ...props
  },
  ref
) {
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!, []);
  const hasText = value != null && String(value) !== '';
  const showClear = onClear != null && hasText && !disabled;
  const active = highlightActive && hasText;
  const answersFind = useFindShortcut(inputRef, findShortcut && !disabled);
  const showKey = answersFind && !hasText && size !== 'compact';
  const clear = () => {
    onClear?.();
    inputRef.current?.focus();
  };

  // Escape empties the box before a dialog's own Escape closes the dialog: Radix listens on the
  // document while capturing, so this listens on the window, earlier still.
  const escape = useRef({ showClear, clear });
  useLayoutEffect(() => {
    escape.current = { showClear, clear };
  });
  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing || e.target !== inputRef.current || !escape.current.showClear) return;
      e.preventDefault();
      e.stopPropagation();
      escape.current.clear();
    };
    window.addEventListener('keydown', onEscape, true);
    return () => window.removeEventListener('keydown', onEscape, true);
  }, []);

  return (
    <div className={cn('relative', className)}>
      <SearchIcon
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-1/2 left-2.5 z-10 -translate-y-1/2',
          active ? 'text-primary' : 'text-muted-foreground',
          ICON_SIZE[size ?? 'default']
        )}
      />
      <Input
        ref={inputRef}
        type="search"
        autoComplete="off"
        size={size}
        value={value}
        disabled={disabled}
        {...props}
        className={cn(
          'pl-8 [&::-webkit-search-cancel-button]:appearance-none',
          showClear && 'pr-8',
          // Room for the chip: ⌘F is narrow, Ctrl+F twice the width.
          showKey && (isMacPlatform() ? 'sm:pr-12' : 'sm:pr-16'),
          filled && 'bg-background',
          active && 'border-primary bg-primary/5 dark:bg-primary/10'
        )}
      />
      {showClear ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="text-muted-foreground"
          // In the box's right end; the tap area reaches 44px for phones.
          layoutClassName="absolute top-1/2 right-1 -translate-y-1/2 after:absolute after:-inset-2.5"
          aria-label="Clear search"
          data-hint="Clear search"
          onClick={clear}
        >
          <XIcon />
        </Button>
      ) : null}
      {showKey ? (
        <Kbd size="sm" className="pointer-events-none absolute top-1/2 right-2 hidden -translate-y-1/2 sm:inline-flex">
          {findShortcutLabel()}
        </Kbd>
      ) : null}
    </div>
  );
});
